import { FaroSimpleTextField } from "@components/common/faro-text-field/faro-simple-text-field";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { NO_TRANSLATE_CLASS, Text } from "@flat-ui";
import { SEARCH_DEBOUNCE_TIME } from "@hooks/use-debounced-search-text";
import { useLanguage } from "@hooks/use-language";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Box,
  SxProps,
  TextFieldVariants,
  Theme,
} from "@mui/material";
import { FetchingStatus } from "@store/store-types";
import { DEFAULT_BOLD_TEXT_FONT_WEIGHT } from "@styles/common-styles";
import {
  AddressSuggestion,
  ARCGIS_LANGUAGE_CODES_MAP,
  geocodeAddress,
  GeocodedAddress,
  getAddressSuggestions,
  isGeocodeAbortError,
} from "@utils/geocoding-utils";
import { useDebounce } from "@utils/time-utils";
import { getAutocompleteStyles } from "@utils/ui-utils";
import { useCallback, useEffect, useRef, useState } from "react";

interface Props {
  /** The current value of the address input field */
  input: string;

  /** Function to update the address input field */
  setInput: (input: string) => void;

  /** The variant of the text field to use */
  variant?: TextFieldVariants;

  /** Whether the autocomplete is disabled */
  isDisabled?: boolean;

  /** The placeholder text to show when the input is empty */
  placeholder?: string;

  /**
   * Whether the input should be focused when the component is rendered.
   * @default true
   */
  shouldAutoFocus?: boolean;

  /** The sx prop to apply custom styles */
  sx?: SxProps<Theme>;

  /** Callback fired when the user sets a geocoded address */
  onGeocodedAddressSelect?: (geocodedAddress: GeocodedAddress) => void;

  /** Callback fired when the user sets a custom address string by pressing Enter */
  onCustomAddressSelect?: (address: string) => void;

  /** Callback fired when the loading state of suggestions changes */
  onLoadingSuggestionsChange?: (state: FetchingStatus) => void;

  /** Callback fired when the loading state of geocoding an address changes */
  onLoadingGeocodedAddressChange?: (state: FetchingStatus) => void;

  /** Callback fired when the user cancels editing by pressing Escape */
  onCancel?: () => void;
}

/**
 * @returns A component that displays an autocomplete input field for addresses.
 * The component uses a geocoding service to get address suggestions and geocode addresses.
 */
export function AddressAutocomplete({
  input,
  setInput,
  variant,
  isDisabled,
  placeholder,
  shouldAutoFocus = true,
  sx,
  onGeocodedAddressSelect,
  onCustomAddressSelect,
  onLoadingSuggestionsChange,
  onLoadingGeocodedAddressChange,
  onCancel,
}: Props): JSX.Element {
  const { currentLanguage } = useLanguage();
  const { handleErrorWithToast } = useErrorContext();
  const styles = getAutocompleteStyles();
  const debouncedInput = useDebounce(input, SEARCH_DEBOUNCE_TIME);
  const lastInputReason = useRef<AutocompleteInputChangeReason>("reset");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const suggestionsAbort = useRef<AbortController | null>(null);
  const langCode = ARCGIS_LANGUAGE_CODES_MAP[currentLanguage.code];

  /**
   * Gets address suggestions for the given input text using the geocoding service.
   * @param text The input text for which to get address suggestions.
   */
  const getSuggestions = useCallback(
    async (text: string) => {
      onLoadingSuggestionsChange?.("pending");

      try {
        // Abort any ongoing suggestions request before starting a new one.
        // This can help prevent any potential race conditions where older requests
        // return after newer requests and overwrite the suggestions with outdated data.
        suggestionsAbort.current?.abort();
        suggestionsAbort.current = new AbortController();
        const response = await getAddressSuggestions({
          text,
          signal: suggestionsAbort.current.signal,
        });
        setSuggestions(response);
        onLoadingSuggestionsChange?.("succeeded");
      } catch (error) {
        const isAbortError = isGeocodeAbortError(error);
        // Only show an error toast if the error is not caused by aborting the request.
        // An aborted request is expected to happen.
        if (!isAbortError) {
          handleErrorWithToast({
            id: `getAddressSuggestions-${Date.now().toString()}`,
            title: "Error loading address suggestions",
            error,
          });
        }
        onLoadingSuggestionsChange?.("rejected");
      }
    },
    [handleErrorWithToast, onLoadingSuggestionsChange]
  );

  /**
   * Gets the geocoded address from the address suggestion selected by the user via a geocoding service.
   * @param addressSuggestion - The address suggestion.
   */
  const getGeocodedAddress = useCallback(
    async (addressSuggestion: AddressSuggestion) => {
      onLoadingGeocodedAddressChange?.("pending");

      try {
        const geocodedAddress = await geocodeAddress({
          addressSuggestion,
          langCode,
        });
        onGeocodedAddressSelect?.(geocodedAddress);
        onLoadingGeocodedAddressChange?.("succeeded");
      } catch (error) {
        handleErrorWithToast({
          id: `getGeocodedAddress-${Date.now().toString()}`,
          title: "Error loading the geocoded address",
          error,
        });
        onLoadingGeocodedAddressChange?.("rejected");
      }
    },
    [
      handleErrorWithToast,
      langCode,
      onGeocodedAddressSelect,
      onLoadingGeocodedAddressChange,
    ]
  );

  /** Triggers getting address suggestions when the debounced input changes and the input is not empty */
  useEffect(() => {
    const sanitizedInput = debouncedInput.trim();
    if (sanitizedInput.length > 0 && lastInputReason.current === "input") {
      void getSuggestions(sanitizedInput);
    }
  }, [debouncedInput, getSuggestions]);

  return (
    <Autocomplete
      inputValue={input}
      onInputChange={(_, newValue, reason) => {
        lastInputReason.current = reason;
        // After the user selects an address suggestion, we still need to fetch the
        // corresponding geocoded address to get the "real" address text that will be
        // displayed in the input. For this reason we skip setting the input here
        // and instead let the getGeocodedAddress function set it.
        if (reason !== "selectOption" && reason !== "reset") {
          setInput(newValue);
        }
        // Clear address suggestions if the sanitized input is cleared
        if (newValue.trim().length === 0) {
          setSuggestions([]);
        }
      }}
      freeSolo
      fullWidth
      options={suggestions}
      // Bypass the default filter of the MUI Autocomplete component. This is needed
      // because the default behavior filters out suggestions that don't contain
      // the input value. But for our use case we want show all suggestions returned
      // by the geocoding service API, even if they don't contain the input value.
      filterOptions={(x) => x}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.label
      }
      onChange={(_, newValue, reason) => {
        // Geocode the address if the user selected a suggestion from the dropdown
        if (
          typeof newValue !== "string" &&
          newValue !== null &&
          reason === "selectOption"
        ) {
          void getGeocodedAddress(newValue);
        }

        // Check if the user selected a custom address by pressing Enter without selecting a suggestion
        if (typeof newValue === "string" && reason === "createOption") {
          onCustomAddressSelect?.(newValue);
        }
      }}
      renderInput={(params) => (
        <FaroSimpleTextField
          {...params}
          placeholder={placeholder}
          autoFocus={shouldAutoFocus}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              onCancel?.();
            }

            // MUI Autocomplete does not trigger the onChange event when the user presses Enter
            // on an empty input. Since we want to allow the user to set an empty string as custom address
            // we need to handle this case here.
            const sanitizedInput = input.trim();
            if (event.key === "Enter" && sanitizedInput.length === 0) {
              onCustomAddressSelect?.(sanitizedInput);
            }
          }}
          variant={variant}
          sx={styles.input.sx}
        />
      )}
      renderOption={(props, option, state) => (
        <Box
          component="li"
          {...props}
          key={`address-item-${state.index}`}
          sx={styles.option.sx}
        >
          <Text
            variant="bodyM"
            className={NO_TRANSLATE_CLASS}
            sx={{
              fontWeight: state.selected
                ? DEFAULT_BOLD_TEXT_FONT_WEIGHT
                : "auto",
            }}
          >
            {option.label}
          </Text>
        </Box>
      )}
      noOptionsText={
        <Box component="div" sx={styles.noOption.sx}>
          No addresses found
        </Box>
      }
      disabled={isDisabled}
      sx={sx}
    />
  );
}
